import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ButtonUI, ContextualMenu, Tabs } from 'sonata-react-components';

import { advanceFiltersNormalizer } from '@/helpers/advanceFilters/advanceFiltersNormalizer.helper';
import { useModals } from '@/router';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { AdvertiserSummary } from '../AdvertiserSummary';

import './entitiesHeader.scss';

import type { TabsItemProps } from 'sonata-react-components';

function getNormalizedFilters(
  rawFilters: Record<string, unknown>
): Record<string, string | number | (string | number)[]> {
  const result: Record<string, string | number | (string | number)[]> = {};
  if (rawFilters.text) {
    result.name = encodeURIComponent(rawFilters.text as string);
  }
  return advanceFiltersNormalizer(rawFilters.advanceFilters as Record<string, any>[] | undefined, result) as Record<
    string,
    string | number | (string | number)[]
  >;
}

export default function EntitiesHeader() {
  const modals = useModals();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  const { advertiserListFilters, cacheSeed, setCacheSeed } = useCampaignStore() as CampaignsStore;

  const defaultTab = 'lineItem';
  const currentTab = searchParams.get('tab') || defaultTab;

  const tabs: TabsItemProps[] = [
    {
      component: <></>,
      id: 'advertiser',
      title: formatMessage({ defaultMessage: 'Advertisers', id: 'campaignPlans.tabs.advertiser' })
    },
    {
      component: <></>,
      id: 'campaignPlan',
      title: formatMessage({ defaultMessage: 'Campaign Plans', id: 'campaignPlans.tabs.cp' })
    },
    {
      component: <></>,
      id: 'insertionOrder',
      title: formatMessage({ defaultMessage: 'Insertion Orders', id: 'campaignPlans.tabs.io' })
    },
    {
      component: <></>,
      id: 'lineItem',
      title: formatMessage({ defaultMessage: 'Line items', id: 'campaignPlans.tabs.lineItems' })
    }
  ];

  const handleTabChange = (tabId: string) => {
    setSearchParams(prevParams => {
      prevParams.set('tab', tabId);
      if (tabId === defaultTab) {
        prevParams.delete('cpId');
      }
      return prevParams;
    });
  };

  const buttons = [
    {
      icon: 'fal fa-plus',
      id: 'campaign-header-create-advertiser-button',
      onClick: () =>
        modals.open('/campaigns/new/advertiserModal', {
          at: { pathname: '/campaigns', search: location.search },
          state: { advertiser: {}, modalId: 'advertiser-modal' }
        }),
      text: formatMessage({ defaultMessage: 'Create Advertiser', id: 'campaignPlans.header.createAdvertiserButton' })
    },
    {
      icon: 'fal fa-plus',
      id: 'campaign-header-create-cp-button',
      onClick: () =>
        modals.open('/campaigns/new/campaignPlanModal', {
          at: { pathname: '/campaigns', search: location.search },
          state: { campaignPlan: null, modalId: 'campaignPlan-modal' }
        }),
      text: formatMessage({ defaultMessage: 'Create Campaign', id: 'campaignPlans.header.createCpButton' })
    },
    {
      icon: 'fal fa-plus',
      id: 'campaign-header-create-io-button',
      onClick: () =>
        modals.open('/campaigns/new/insertionOrderModal', {
          at: { pathname: '/campaigns', search: location.search },
          state: { modalId: 'insertionOrder-modal' }
        }),
      text: formatMessage({ defaultMessage: 'Create Insertion Order', id: 'campaignPlans.header.createIoButton' })
    },
    {
      icon: 'fal fa-plus',
      id: 'campaign-header-create-LineItem-button',
      onClick: () =>
        modals.open('/campaigns/new/lineItemModal', {
          at: { pathname: '/campaigns', search: location.search },
          state: { modalId: 'lineItem-modal' }
        }),
      text: formatMessage({ defaultMessage: 'Create Line Item', id: 'campaignPlans.header.createLineItemButton' })
    }
  ];

  return (
    <header className="entitiesHeader">
      <div className="entitiesHeader__row">
        <div className="entitiesHeader__tabsWrapper">
          <Tabs className="entitiesHeaderTabs" onChangeTab={handleTabChange} tabSelected={currentTab} tabs={tabs} />
        </div>
        <div className="entitiesHeader__metrics">
          {currentTab === 'advertiser' ? (
            <AdvertiserSummary cacheSeed={cacheSeed} filters={getNormalizedFilters(advertiserListFilters)} />
          ) : null}
        </div>
        <div className="entitiesHeader__actions">
          <ContextualMenu
            buttonConfig={{
              icon: 'fas fa-plus',
              shape: 'squareRounded',
              size: 'medium',
              text: formatMessage({ defaultMessage: 'Add New', id: 'campaignPlan.header.addNew' }),
              type: 'primary'
            }}
            buttons={buttons}
            direction="vertical"
            id={'campaign-header-add-button'}
          />
          <ButtonUI
            icon="fa-solid fa-arrows-rotate-reverse"
            iconPosition="left"
            onClick={setCacheSeed}
            size="medium"
            type="secondary"
            visible
          />
        </div>
      </div>
    </header>
  );
}
